import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import classnames from 'classnames';
import { Grid } from '@material-ui/core';
import { useLayoutContext } from '../Layout';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { WithSubComponents } from '@forvalter/utils/TypeScriptUtils';
import { MainNavigation } from '../MainNavigation';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { BreadcrumbsMain } from '@klp/breadcrumbs/BreadcrumbsMain';
import { BreadcrumbsCrumb } from '@klp/breadcrumbs/BreadcrumbsCrumb';
import { getConfig } from '@klpkonsern/appconfig';

const styles = () => ({
    centered: {
        alignItems: 'center',
    },
});

type MainProps = {
    decorate?: boolean;
    withoutSidebar?: boolean;
    withoutHeader?: boolean;
    centered?: boolean;
    fullScreen?: boolean;
    withBackground?: boolean;
};

type MainSubComponents = {
    Navigation: typeof MainNavigation;
    Breadcrumbs: typeof BreadcrumbsMain;
    Crumb: typeof BreadcrumbsCrumb;
};

export const Main: WithSubComponents<
    React.FC<PropsWithChildren<MainProps>>,
    MainSubComponents
> = ({
    decorate = false,
    withoutSidebar = false,
    withoutHeader = false,
    fullScreen = false,
    centered = false,
    withBackground = false,
    children,
}) => {
    const classes = useKlpStyles({}, styles);
    const layoutContext = useLayoutContext();

    useEffect(() => {
        layoutContext.setDecorate(decorate);
        layoutContext.setWithoutSidebar(withoutSidebar);
        layoutContext.setWithoutHeader(withoutHeader);
        layoutContext.setFullScreen(fullScreen);
        layoutContext.setCentered(centered);
        layoutContext.setWithBackground(withBackground);
    }, [
        decorate,
        withoutSidebar,
        withoutHeader,
        fullScreen,
        centered,
        withBackground,
        layoutContext,
    ]);

    const classNames = classnames({
        [classes.centered]: centered,
    });

    const errorMessageEnabled = getConfig(
        'WEBAPP_APP_ERROR_MESSAGE_ENABLE',
        false,
    );

    return (
        <Grid
            item={true}
            xs={12}
            className={classNames}
            data-testid="MainContent"
        >
            {errorMessageEnabled && <ErrorMessage />}
            {children}
        </Grid>
    );
};

Main.Navigation = MainNavigation;
Main.Breadcrumbs = BreadcrumbsMain;
Main.Crumb = BreadcrumbsCrumb;
